import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';

import { ReviewStreamTabs } from '../components/ReviewStreamTabs';
import { useAuth } from '../context/AuthContext';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import {
  getDefaultStreamForSubject,
  isCoreSubjectCode,
  SUBJECT_CONFIG,
  type LearningEpisodeStream,
} from '../utils/subjectConfig';
import {
  reviewService,
  type DailyReviewProgress,
  type ReviewHomeSurface,
} from '../services/reviewService';

export function ReviewHomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { activeSubject: subject } = useActiveSubject();
  const [stream, setStream] = useState<LearningEpisodeStream>(() => getDefaultStreamForSubject(subject));
  const [surface, setSurface] = useState<ReviewHomeSurface | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dailyReviewProgress, setDailyReviewProgress] = useState<DailyReviewProgress | null>(null);
  const surfaceRequestSeqRef = useRef(0);

  useEffect(() => {
    if (!isCoreSubjectCode(subject)) {
      navigate('/', { replace: true });
    }
  }, [navigate, subject]);

  const loadDailyReviewProgress = useCallback(async () => {
    if (!user?.id || !isCoreSubjectCode(subject)) {
      return;
    }

    const cached = reviewService.peekDailyReviewProgress(user.id);
    if (cached) {
      setDailyReviewProgress(cached);
      return;
    }

    try {
      const nextProgress = await reviewService.loadDailyReviewProgress(user.id, { peekMissingSurfaces: true });
      setDailyReviewProgress(nextProgress);
    } catch {
      setDailyReviewProgress(null);
    }
  }, [user?.id]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    const requestId = ++surfaceRequestSeqRef.current;

    setLoading(true);
    setError(null);
    try {
      const raw = await reviewService.loadHomeReviewSurface({
        userId: user.id,
        subjectFilter: subject,
        stream,
      });
      if (requestId !== surfaceRequestSeqRef.current) {
        return;
      }
      setSurface(await reviewService.ensureLearningEpisodeSessionSurface(raw));
    } catch (loadError) {
      if (requestId !== surfaceRequestSeqRef.current) {
        return;
      }
      setError(loadError instanceof Error ? loadError.message : '加载复习面失败');
    } finally {
      if (requestId === surfaceRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [stream, subject, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadDailyReviewProgress();
  }, [loadDailyReviewProgress, subject]);

  useEffect(() => {
    setStream((current) => {
      const next = getDefaultStreamForSubject(subject);
      return current === next ? current : next;
    });
  }, [subject]);

  async function handleStreamChange(nextStream: LearningEpisodeStream) {
    if (!user?.id) return;
    setStream(nextStream);
    setLoading(true);
    const requestId = ++surfaceRequestSeqRef.current;
    try {
      const next = await reviewService.swapStreamLearningEpisodeSurface({
        userId: user.id,
        subject,
        stream: nextStream,
        previousPlan: surface?.sequencePlan,
      });
      if (requestId !== surfaceRequestSeqRef.current) {
        return;
      }
      setSurface(next);
    } catch (swapError) {
      if (requestId !== surfaceRequestSeqRef.current) {
        return;
      }
      setError(swapError instanceof Error ? swapError.message : '换组失败');
    } finally {
      if (requestId === surfaceRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }

  const pendingCount = surface?.sequencePlan?.pendingCount ?? 0;
  const subjectLabel = SUBJECT_CONFIG[subject].label;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">复习</h1>
          <p className="mt-1 text-sm text-muted">选择学科与序列流，进入 LEC 卡族复习。</p>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          刷新
        </button>
      </div>

      <section className="rounded-2xl border border-border bg-surface p-5 shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-ink">当前学科</span>
          <span className="text-sm text-muted">{subjectLabel}</span>
          <span className="text-xs text-muted">（可在顶部导航切换）</span>
        </div>

        <div className="mt-4">
          <ReviewStreamTabs
            subject={subject}
            stream={stream}
            onStreamChange={(nextStream) => void handleStreamChange(nextStream)}
            dailyReviewProgress={dailyReviewProgress}
          />
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" /></div>
      ) : error ? (
        <p className="text-danger">{error}</p>
      ) : (
        <section className="rounded-2xl border border-border bg-surface p-5 shadow-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm text-muted">待复习套数 · {subjectLabel}</div>
              <div className="mt-1 text-3xl font-semibold text-ink">{pendingCount}</div>
            </div>
            <Sparkles className="text-primary" size={28} />
          </div>
          {surface?.sequencePlan?.episode.summary ? (
            <p className="mt-4 text-sm text-muted">{surface.sequencePlan.episode.summary}</p>
          ) : null}
          {surface?.unavailableReason ? (
            <p className="mt-4 text-sm text-amber-700">{surface.unavailableReason}</p>
          ) : null}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={pendingCount <= 0}
              onClick={() => navigate('/review/session', { state: { subject, stream } })}
              className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            >
              开始复习
            </button>
            <Link to="/review/sequence" className="rounded-lg border border-border px-5 py-2.5 text-sm">学习序列设置</Link>
            <Link to="/learning/footprint" className="rounded-lg border border-border px-5 py-2.5 text-sm">学习足迹</Link>
            <Link to="/bookmarks" className="rounded-lg border border-border px-5 py-2.5 text-sm">收藏</Link>
          </div>
        </section>
      )}
    </div>
  );
}
